import {
  Invitation,
  Inviter,
  Registerer,
  RegistererState,
  SessionState,
  UserAgent,
} from 'sip.js';
import VoiceAPI from './voiceAPIClient';

const createDisconnectedEvent = () => new CustomEvent('call:disconnected');

class RuutVoiceClient extends EventTarget {
  constructor() {
    super();
    this.userAgent = null;
    this.registerer = null;
    this.activeSession = null;
    this.pendingInvitation = null;
    this.inboxId = null;
    this.initialized = false;
    this.initializationPromise = null;
    this.mediaRecoveryTimer = null;
  }

  async initializeDevice(inboxId) {
    if (this.inboxId === inboxId && this.isRegistered) return this.userAgent;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this.startDevice(inboxId);
    try {
      return await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  async startDevice(inboxId) {
    await this.destroyDevice();
    const config = await VoiceAPI.getToken(inboxId);
    if (config?.provider !== 'ruut_voice')
      throw new Error('Invalid Ruut Voice session');

    this.userAgent = new UserAgent({
      uri: UserAgent.makeURI(config.aor),
      transportOptions: { server: config.websocket_url },
      authorizationUsername: config.authorization_username,
      authorizationPassword: config.authorization_password,
      displayName: config.display_name,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: { iceServers: config.ice_servers || [] },
      },
      delegate: {
        onInvite: invitation => this.handleInvitation(invitation),
      },
    });

    this.registerer = new Registerer(this.userAgent);
    await this.userAgent.start();
    await this.registerer.register();
    this.inboxId = inboxId;
    this.initialized = true;
    return this.userAgent;
  }

  get isRegistered() {
    return this.registerer?.state === RegistererState.Registered;
  }

  // True when the provider delivered an inbound INVITE straight to this
  // client (e.g. a ring-group dial) that nobody has answered yet. The join
  // flow uses this to accept the existing leg instead of asking the backend
  // to dial the agent again.
  get hasPendingInvitation() {
    return !!this.pendingInvitation;
  }

  get hasActiveConnection() {
    return !!this.activeSession;
  }

  async joinClientCall() {
    const invitation = await this.waitForInvitation();
    await invitation.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });
    this.activeSession = invitation;
    this.pendingInvitation = null;
    this.attachRemoteAudio(invitation);
    this.observeMediaConnection(invitation);
    return invitation;
  }

  setMuted(shouldMute) {
    const peer = this.activeSession?.sessionDescriptionHandler?.peerConnection;
    peer?.getSenders().forEach(sender => {
      if (sender.track?.kind === 'audio') sender.track.enabled = !shouldMute;
    });
    return shouldMute;
  }

  async endClientCall() {
    const session = this.activeSession || this.pendingInvitation;
    if (session) {
      try {
        if (session.state === SessionState.Established) await session.bye();
        else if (session instanceof Inviter) await session.cancel();
        else if (session instanceof Invitation) await session.reject();
      } catch (error) {
        // The remote leg may already be gone; local cleanup still has to finish.
      }
    }
    this.activeSession = null;
    this.pendingInvitation = null;
    window.clearTimeout(this.mediaRecoveryTimer);
    this.mediaRecoveryTimer = null;
  }

  async destroyDevice() {
    await this.endClientCall();
    try {
      if (this.registerer) await this.registerer.unregister();
      if (this.userAgent) await this.userAgent.stop();
    } catch (error) {
      // A disconnected transport is already effectively destroyed.
    }
    this.registerer = null;
    this.userAgent = null;
    this.inboxId = null;
    this.initialized = false;
  }

  handleInvitation(invitation) {
    // One-call-at-a-time client. A concurrent ring-group call can deliver a
    // second INVITE while we already hold an active or pending session; reject
    // it with 486 Busy Here instead of letting its lifecycle clobber the live
    // call (previously the second INVITE's termination nulled activeSession and
    // fired call:disconnected, dropping the established call). The switch
    // (AgentBusyManager) prevents the second INVITE for ring-group dials, but
    // this is defense-in-depth for any stray second leg.
    if (this.activeSession || this.pendingInvitation) {
      invitation.reject({ statusCode: 486 }).catch(() => {});
      return;
    }

    this.pendingInvitation = invitation;
    invitation.stateChange.addListener(state => {
      if (state !== SessionState.Terminated) return;
      // Only clear state owned by this invitation — never the live session of a
      // different call.
      if (this.activeSession === invitation) this.activeSession = null;
      if (this.pendingInvitation === invitation) this.pendingInvitation = null;
      this.dispatchEvent(createDisconnectedEvent());
    });
  }

  waitForInvitation() {
    if (this.pendingInvitation) return Promise.resolve(this.pendingInvitation);

    return new Promise((resolve, reject) => {
      const deadline = window.setTimeout(
        () => reject(new Error('Ruut Voice did not deliver the agent call')),
        15000
      );
      const poll = window.setInterval(() => {
        if (!this.pendingInvitation) return;
        window.clearTimeout(deadline);
        window.clearInterval(poll);
        resolve(this.pendingInvitation);
      }, 100);
    });
  }

  // Session media is attached here to keep lifecycle ownership in one client.
  // eslint-disable-next-line class-methods-use-this
  attachRemoteAudio(session) {
    const peer = session.sessionDescriptionHandler?.peerConnection;
    if (!peer) return;

    const stream = new MediaStream();
    peer.getReceivers().forEach(receiver => {
      if (receiver.track) stream.addTrack(receiver.track);
    });
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.srcObject = stream;
    audio.play().catch(() => {});
  }

  observeMediaConnection(session) {
    const peer = session.sessionDescriptionHandler?.peerConnection;
    if (!peer) return;

    const handleStateChange = () => {
      const state = peer.connectionState;
      if (state === 'connected') {
        window.clearTimeout(this.mediaRecoveryTimer);
        this.mediaRecoveryTimer = null;
        this.dispatchEvent(new CustomEvent('call:reconnected'));
        return;
      }

      if (state === 'disconnected') {
        this.dispatchEvent(new CustomEvent('call:reconnecting'));
        window.clearTimeout(this.mediaRecoveryTimer);
        this.mediaRecoveryTimer = window.setTimeout(() => {
          if (peer.connectionState !== 'disconnected') return;
          this.activeSession = null;
          this.dispatchEvent(createDisconnectedEvent());
        }, 8000);
        return;
      }

      if (state === 'failed' || state === 'closed') {
        window.clearTimeout(this.mediaRecoveryTimer);
        this.mediaRecoveryTimer = null;
        this.activeSession = null;
        this.dispatchEvent(createDisconnectedEvent());
      }
    };
    peer.addEventListener('connectionstatechange', handleStateChange);
  }
}

export default new RuutVoiceClient();
